import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showAppError } from '@/lib/errors';
import { SeekLayout } from '@/components/seek/SeekLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useSeekAuth } from '@/contexts/SeekAuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { seekSupabase } from '@/integrations/supabase/seekClient';
import {
  bindPaymentIntentKeyToTransaction,
  paymentErrorMessage,
  paymentIntentKey,
  settlePaymentIntentKey,
  type PaymentIntentKeyInput,
} from '@/lib/paymentSafety';
import { Package, Coins, ArrowUpRight, CheckCircle2, Clock, AlertCircle, XCircle, BarChart3, BrainCircuit, BriefcaseBusiness, Sparkles, Search, ChevronDown, ShoppingCart, Loader2, Minus, Plus, Trash2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatLocalizedDate } from '@/lib/localizedDate';
import SarSymbol from '@/components/SarSymbol';
import { toast } from 'sonner';
import { CandidateDowngradeSection } from '@/components/seek/subscription/CandidateDowngradeSection';
import { PaymentReturnBanner } from '@/components/billing/PaymentReturnBanner';
import { InvoiceViewerDialog } from '@/components/billing/InvoiceViewerDialog';
import { BillingHistoryCard } from '@/components/billing/BillingHistoryCard';
import { PlanBundleShowcase } from '@/components/billing/PlanBundleShowcase';
import { getPlanBundleServices, type PlanBundleServiceLike } from '@/lib/billing/planBundleServices';
import { useCandidateEntitlement } from '@/hooks/useEntitlement';
import { CheckoutPurchaseDialog } from '@/components/billing/CheckoutPurchaseDialog';
import { buildBillingConsentBinding, CONSENT_TEXT_VERSION } from '@/lib/billing/consentPaymentBinding';
import {
  buildCreditPricingBinding,
  buildQuoteBoundCreditPaymentAttempt,
} from '@/lib/billing/creditPricingBinding';
import {
  creditCheckoutResponseError,
  isCreditPricingChangedError,
  recoverCreditPricingChange,
} from '@/lib/billing/creditPricingChangedRecovery';
import type { MoyasarPaymentFormConfig } from '@/components/billing/MoyasarPaymentForm';
import { SecurePaymentDialog } from '@/components/billing/SecurePaymentDialog';
import { BillingConfirmationDialog } from '@/components/billing/BillingConfirmationDialog';
import { CreditCartLinePricing, CreditCartPricing, CreditCartPricingStatus, CreditPackGrossPrice, PlanGrossPrice } from '@/components/billing/CreditPricingDisplay';
import { useCreditCartPricing } from '@/hooks/useCreditCartPricing';
import { ActiveSubscriptionPlanChangeDialog } from '@/components/billing/ActiveSubscriptionPlanChangeDialog';
import { RecurringPaymentMethodDialog } from '@/components/billing/RecurringPaymentMethodDialog';
import { SubscriptionCancellationManagement } from '@/components/billing/SubscriptionCancellationManagement';
import { resolveAuthoritativeCancellationDate } from '@/lib/billing/subscriptionCancellation';
import { classifyPaidPlanSelection } from '@/lib/billing/planChangeRouting';
import { buildFunctionUrl, getSupabasePublishableKey } from '@/lib/selfHostedUrls';
import {
  activateMockPaidCheckout,
  canActivateMockPaidCheckout,
  getCheckoutRedirectUrl,
} from '@/lib/billing/paymentCheckout';
import type { ForkSeekUniversalWalletRow } from '@/integrations/supabase/creditMarketplaceTypes';
import type { Database } from '@/integrations/supabase/types';
import { useCreditPaymentIntentReconciliation } from '@/hooks/useCreditPaymentIntentReconciliation';
import {
  buildBillingHistoryEntries,
  type BillingHistoryInvoice,
  type BillingHistoryPayment,
} from '@/lib/billing/billingHistory';
import { fetchCustomerBillingHistory } from '@/lib/billing/customerBillingHistory';
import { usePricingDisplayCatalog } from '@/hooks/usePricingDisplay';
import { planIntroPricing, planPricingForCycle } from '@/lib/billing/pricingDisplay';
import {
  billingConfirmationRequiredError,
  isBillingConfirmationRequiredError,
} from '@/lib/billing/candidateBillingConfirmation';
import {
  findSeekPlanContinuation,
  isSeekPlanContinuationReady,
  removeSeekPlanContinuation,
} from '@/lib/seekPlanContinuation';
import {
  CUSTOMER_CANDIDATE_SUBSCRIPTION_SELECT,
  CUSTOMER_CREDIT_BALANCE_SELECT,
  CUSTOMER_CREDIT_PACK_SELECT,
  CUSTOMER_PLAN_SERVICE_SELECT,
  CUSTOMER_SEEK_PLAN_SELECT,
  CUSTOMER_SEEK_WALLET_SELECT,
} from '@/lib/billing/customerBillingSelectors';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

function SeekBillingSectionError({ title, message, retryLabel, onRetry }: { title: string; message: string; retryLabel: string; onRetry: () => void }) {
  return (
    <Alert variant="destructive" className="border-destructive/40">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className="space-y-3">
        <p>{message}</p>
        <Button type="button" size="sm" variant="outline" onClick={onRetry}>
          {retryLabel}
        </Button>
      </AlertDescription>
    </Alert>
  );
}

const SEEK_USAGE_ICONS: Record<string, React.ElementType> = {
  cv_review: BrainCircuit,
  cv_builder: BriefcaseBusiness,
  interview_practice: Sparkles,
  job_discovery: Search,
};

function SeekUsageServiceCard({
  candidateId,
  serviceKey,
  serviceName,
}: {
  candidateId: string | undefined;
  serviceKey: string;
  serviceName: string;
}) {
  const { t } = useTranslation();
  const entitlement = useCandidateEntitlement(serviceKey, candidateId);
  const Icon = SEEK_USAGE_ICONS[serviceKey] ?? BarChart3;
  const used = entitlement.used ?? 0;
  const limit = entitlement.limit ?? 0;
  const creditBalance = entitlement.creditBalance ?? 0;
  const pct = entitlement.isUnlimited || limit <= 0 ? 0 : Math.min(Math.round((used / limit) * 100), 100);
  const isOver = !entitlement.isUnlimited && limit > 0 && used >= limit;
  const isNear = !entitlement.isUnlimited && limit > 0 && pct >= 80;

  if (entitlement.isLoading) {
    return <Skeleton className="h-28 rounded-lg" />;
  }

  return (
    <div className="space-y-2 rounded-lg border border-border bg-background p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-seek/10">
            <Icon className="h-4 w-4 text-seek" />
          </div>
          <span className="min-w-0 truncate text-sm font-semibold text-foreground">{serviceName}</span>
        </div>
        <span className="shrink-0 font-mono text-sm text-foreground">
          {used} / {entitlement.isUnlimited ? '∞' : limit}
          {creditBalance > 0 && <span className="ms-1 text-seek">+{creditBalance}</span>}
        </span>
      </div>
      {!entitlement.isUnlimited && limit > 0 && (
        <Progress
          value={pct}
          className={`h-1.5 ${isOver ? '[&>div]:bg-destructive' : isNear ? '[&>div]:bg-amber-500' : '[&>div]:bg-seek'}`}
        />
      )}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {entitlement.isUnlimited && (
          <span className="inline-flex items-center gap-1 text-seek">
            <Sparkles className="h-3 w-3" />{t('seek.subscription.unlimited')}
          </span>
        )}
        {entitlement.isUsingCredits && (
          <span className="rounded-full bg-seek/10 px-2 py-0.5 text-seek">
            {t('seek.subscription.usingCredits')}
          </span>
        )}
        {creditBalance > 0 && (
          <span className="text-muted-foreground">
            {t('seek.subscription.creditTopupAvailable', { count: creditBalance })}
          </span>
        )}
      </div>
    </div>
  );
}

interface SeekPlan {
  id: string;
  plan_key: string;
  plan_name: string;
  description: string | null;
  price_monthly_sar: number | null;
  standard_price_sar: number | null;
  intro_price_sar: number | null;
  intro_offer_enabled: boolean | null;
  price_yearly_sar: number | null;
  price_annual_sar: number | null;
  yearly_billing_enabled: boolean | null;
  platform: string | null;
  is_free: boolean | null;
  is_popular: boolean | null;
}

interface SeekSubscriptionRecord {
  id: string;
  status: string;
  cancellation_requested_at: string | null;
  cancellation_effective_at: string | null;
  cancellation_cleared_downgrade_plan_id: string | null;
  auto_renew: boolean;
  billing_cycle: string;
  renewal_date: string | null;
  billing_period_start: string | null;
  billing_period_end: string | null;
  paid_through_date: string | null;
  ends_at: string | null;
  payment_method: string | null;
  downgrade_plan_id: string | null;
  plans: SeekPlan | null;
}

interface CreditBalance {
  id: string;
  credit_type: string;
  balance: number | string;
}

type CreditPack = Pick<
  Database['public']['Tables']['credit_pack_products']['Row'],
  'id' | 'name' | 'credit_type' | 'credits_included' | 'price_sar' | 'valid_days'
>;

interface CreditCartItem {
  pack: CreditPack;
  quantity: number;
}

function hasCreditPack(item: { pack: CreditPack | undefined; quantity: number }): item is CreditCartItem {
  return item.pack !== undefined;
}

interface PaymentTransaction extends BillingHistoryPayment {
  id: string;
  payment_type: string | null;
  status: string;
  total_sar: number | string | null;
  created_at: string;
}

type SeekPurchaseState = 'select' | 'payment' | 'processing';

interface InvoiceRecord extends BillingHistoryInvoice {
  id: string;
  invoice_number?: string | null;
  payment_transaction_id: string | null;
  amount_sar?: number | string | null;
  total_sar?: number | string | null;
  status?: string | null;
  issued_at?: string | null;
  created_at?: string | null;
}

export default function SeekSubscription() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const { user } = useSeekAuth();
  const {
    data: pricingCatalog,
    isLoading: pricingCatalogLoading,
    isError: pricingCatalogError,
  } = usePricingDisplayCatalog(seekSupabase, 'forkseek', !!user);
  const candidateId = user?.id;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceRecord | null>(null);
  const [selectedPurchasePlan, setSelectedPurchasePlan] = useState<SeekPlan | null>(null);
  const [selectedPlanChangeTarget, setSelectedPlanChangeTarget] = useState<SeekPlan | null>(null);
  const [billingConsent, setBillingConsent] = useState(false);
  const [planBillingConfirmationOpen, setPlanBillingConfirmationOpen] = useState(false);
  const [planBillingConfirmed, setPlanBillingConfirmed] = useState(false);
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [purchaseState, setPurchaseState] = useState<SeekPurchaseState>('select');
  const [paymentForm, setPaymentForm] = useState<MoyasarPaymentFormConfig | null>(null);
  const [creditCart, setCreditCart] = useState<Record<string, number>>({});
  const [creditBillingDialogOpen, setCreditBillingDialogOpen] = useState(false);
  const [creditConfirmationRevision, setCreditConfirmationRevision] = useState(0);
  const [creditPaymentForm, setCreditPaymentForm] = useState<MoyasarPaymentFormConfig | null>(null);
  const creditCartRef = useRef<HTMLDivElement | null>(null);
  const purchaseRequestInFlightRef = useRef(false);
  const handledPlanContinuationRef = useRef<string | null>(null);

  const {
    data: subscription,
    isLoading: subLoading,
    isError: subError,
    error: subscriptionError,
    refetch: refetchSubscription,
  } = useQuery<SeekSubscriptionRecord | null>({
    queryKey: ['seek-subscription', candidateId],
    queryFn: async () => {
      const { data, error } = await seekSupabase
        .from('candidate_subscriptions')
        .select(CUSTOMER_CANDIDATE_SUBSCRIPTION_SELECT)
        .eq('candidate_id', candidateId!)
        .in('status', ['active', 'payment_failed', 'downgraded', 'expired'])
        .order('starts_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as SeekSubscriptionRecord | null;
    },
    enabled: !!candidateId,
  });

  const {
    data: credits = [],
    isLoading: creditsLoading,
    isError: creditsError,
    error: creditsQueryError,
    refetch: refetchCredits,
  } = useQuery<CreditBalance[]>({
    queryKey: ['seek-credits', candidateId],
    queryFn: async () => {
      const { data, error } = await seekSupabase
        .from('credit_balances')
        .select(CUSTOMER_CREDIT_BALANCE_SELECT)
        .eq('owner_type', 'candidate')
        .eq('owner_id', candidateId!);
      if (error) throw error;
      return (data ?? []) as CreditBalance[];
    },
    enabled: !!candidateId,
  });

  const {
    data: universalWalletRead = { wallet: null, error: null },
    isLoading: universalWalletLoading,
    refetch: refetchUniversalWallet,
  } = useQuery<{
    wallet: Pick<ForkSeekUniversalWalletRow, 'balance'> | null;
    error: string | null;
  }>({
    queryKey: ['forkseek-universal-wallet', candidateId],
    queryFn: async () => {
      const { data, error } = await seekSupabase
        .from('forkseek_credit_wallets')
        .select(CUSTOMER_SEEK_WALLET_SELECT)
        .eq('candidate_id', candidateId!)
        .maybeSingle();

      if (error) return { wallet: null, error: error.message };
      return { wallet: (data ?? null) as Pick<ForkSeekUniversalWalletRow, 'balance'> | null, error: null };
    },
    enabled: !!candidateId,
  });

  const {
    data: billingHistory = { payments: [], invoices: [] },
    isLoading: billingHistoryLoading,
    isError: billingHistoryError,
    error: billingHistoryQueryError,
    refetch: refetchBillingHistory,
  } = useQuery({
    queryKey: ['seek-customer-billing-history-recent', candidateId],
    queryFn: () => fetchCustomerBillingHistory(seekSupabase, {
      ownerType: 'candidate',
      ownerId: candidateId!,
      limit: 3,
    }),
    enabled: !!candidateId,
  });
  const transactions = billingHistory.payments as PaymentTransaction[];
  const invoices = billingHistory.invoices as InvoiceRecord[];

  const {
    data: creditPacks = [],
    isLoading: creditPacksLoading,
    error: creditPacksError,
    refetch: refetchCreditPacks,
  } = useQuery<CreditPack[]>({
    queryKey: ['credit-packs-forkseek'],
    queryFn: async () => {
      const { data, error } = await seekSupabase
        .from('credit_pack_products')
        .select(CUSTOMER_CREDIT_PACK_SELECT)
        .eq('platform', 'forkseek')
        .eq('is_active', true)
        .is('archived_at', null)
        .order('price_sar', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const {
    data: seekPlans = [],
    isLoading: plansLoading,
    isError: plansError,
    refetch: refetchPlans,
  } = useQuery<SeekPlan[]>({
    queryKey: ['seek-plans'],
    queryFn: async () => {
      const { data, error } = await seekSupabase
        .from('plans')
        .select(CUSTOMER_SEEK_PLAN_SELECT)
        .in('platform', ['forkseek', 'seek'])
        .eq('is_active', true)
        .order('price_monthly_sar', { ascending: true });
      if (error) throw error;
      return (data ?? []) as SeekPlan[];
    },
  });
  const planIds = seekPlans.map((p) => p.id);
  const { data: planServicesData = [] } = useQuery<PlanBundleServiceLike[]>({
    queryKey: ['seek-subscription-plan-services', planIds],
    queryFn: async () => {
      const { data, error } = await seekSupabase
        .from('plan_services')
        .select(CUSTOMER_PLAN_SERVICE_SELECT)
        .in('plan_id', planIds)
        .eq('is_enabled', true)
        .order('usage_limit', { ascending: false });
      if (error) throw error;
      return (data ?? []) as PlanBundleServiceLike[];
    },
    enabled: planIds.length > 0,
  });

  const cancelMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await seekSupabase.functions.invoke('cancel-subscription', {
        body: { subscription_id: subscription?.id, owner_type: 'candidate' },
      });
      if (error) throw error;
      if (data?.success === false) throw new Error(data.user_message || data.error || t('seek.subscription.cancellationFailed'));
      return data;
    },
    onSuccess: (data) => {
      toast.success(t('seek.subscription.cancellationConfirmed', { date: data.effective_at || t('seek.subscription.endOfPeriod') }));
      queryClient.invalidateQueries({ queryKey: ['seek-subscription'] });
    },
    onError: (err: Error) => {
      showAppError(err, { product: 'forkseek', domain: 'subscription' });
    },
  });

  const revokeCancelMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await seekSupabase.functions.invoke('cancel-subscription', {
        body: { subscription_id: subscription?.id, owner_type: 'candidate', action: 'revoke_cancel' },
      });
      if (error) throw error;
      if (data?.success === false) throw new Error(data.user_message || data.error || t('seek.subscription.revokeFailed'));
      return data;
    },
    onSuccess: () => {
      toast.success(t('seek.subscription.cancellationRevoked'));
      queryClient.invalidateQueries({ queryKey: ['seek-subscription'] });
    },
    onError: (err: Error) => {
      showAppError(err, { product: 'forkseek', domain: 'subscription' });
    },
  });

  const plan = subscription?.plans;
  const activeBillingCycle = subscription?.billing_cycle === 'yearly' ? 'yearly' : 'monthly';
  const purchaseBillingCycle = subscription?.status === 'active' ? activeBillingCycle : 'monthly';
  const paidPlanSelectionRoute = classifyPaidPlanSelection(subscription ? {
    status: subscription.status,
    currentPlanIsFree: plan?.is_free,
    billingCycle: subscription.billing_cycle,
    billingPeriodStart: subscription.billing_period_start,
    billingPeriodEnd: subscription.billing_period_end,
    paidThroughDate: subscription.paid_through_date,
    cancellationEffectiveAt: subscription.cancellation_effective_at,
  } : null);
  useCreditPaymentIntentReconciliation({
    client: seekSupabase,
    ownerType: 'candidate',
    ownerId: candidateId,
  });
  const getPlanPrice = useCallback((p: SeekPlan, billingCycle: 'monthly' | 'yearly' = 'monthly') => {
    if (billingCycle === 'yearly') {
      const yearly = Number(p.price_yearly_sar);
      const annual = Number(p.price_annual_sar);
      if (Number.isFinite(yearly) && yearly > 0) return yearly;
      if (Number.isFinite(annual) && annual > 0) return annual;
      return null;
    }
    const standard = Number(p.standard_price_sar);
    const monthly = Number(p.price_monthly_sar);
    if (Number.isFinite(standard) && standard > 0) return standard;
    if (Number.isFinite(monthly) && monthly > 0) return monthly;
    return null;
  }, []);
  const getIntroPrice = (p: SeekPlan) => {
    const intro = Number(p.intro_price_sar);
    return p.intro_offer_enabled === true && Number.isFinite(intro) && intro > 0 ? intro : null;
  };
  const planServicesMap: Record<string, PlanBundleServiceLike[]> = {};
  planServicesData.forEach((ps) => {
    if (!planServicesMap[ps.plan_id]) planServicesMap[ps.plan_id] = [];
    planServicesMap[ps.plan_id].push(ps);
  });
  const visiblePlans = seekPlans
    .slice()
    .sort((a, b) => {
      if (a.is_free && !b.is_free) return -1;
      if (!a.is_free && b.is_free) return 1;
      return (getPlanPrice(a, purchaseBillingCycle) ?? Number.MAX_SAFE_INTEGER) -
        (getPlanPrice(b, purchaseBillingCycle) ?? Number.MAX_SAFE_INTEGER);
    });
  const currentPlanPrice = plan ? getPlanPrice(plan as SeekPlan, activeBillingCycle) : null;
  const nextUpgradePlan = currentPlanPrice == null
    ? undefined
    : visiblePlans.find((candidate) => {
        const candidatePrice = getPlanPrice(candidate, activeBillingCycle);
        return !candidate.is_free && candidatePrice != null && candidatePrice > currentPlanPrice;
      });
  const getCreditPackId = (pack: Pick<CreditPack, 'id'>) => pack.id;
  const findCreditPackById = (packId: string) =>
    creditPacks.find((pack) => getCreditPackId(pack) === packId);
  const creditCartItems = Object.entries(creditCart)
    .map(([packId, quantity]) => ({
      pack: findCreditPackById(packId),
      quantity,
    }))
    .filter((item) => item.quantity > 0)
    .filter(hasCreditPack);
  const creditCartPricingItems = creditCartItems.map((item) => ({
    packId: String(item.pack.id),
    quantity: item.quantity,
  }));
  const creditCartPricing = useCreditCartPricing('forkseek', creditCartPricingItems);
  const creditCartTotal = creditCartItems.reduce(
    (sum, item) => sum + Number(item.pack.price_sar) * item.quantity,
    0,
  );
  const addCreditPackToCart = (packId: unknown, packName?: string | null) => {
    const normalizedPackId = String(packId ?? '');
    if (!normalizedPackId) return;
    setCreditCart((prev) => ({
      ...prev,
      [normalizedPackId]: (prev[normalizedPackId] ?? 0) + 1,
    }));
    toast.success(t('seek.credits.cart.addedToCart'), {
      description: packName ?? t('seek.credits.cart.title'),
    });
    window.requestAnimationFrame(() => {
      creditCartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };
  const setCreditPackQuantity = (packId: unknown, quantity: number) => setCreditCart((prev) => {
    const normalizedPackId = String(packId ?? '');
    if (!normalizedPackId) return prev;
    const next = { ...prev };
    if (quantity <= 0) delete next[normalizedPackId];
    else next[normalizedPackId] = quantity;
    return next;
  });
  const clearCreditCart = () => setCreditCart({});
  const adaptivePlanGridStyle = { gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 22rem), 1fr))' };
  const selectedPurchasePrice = selectedPurchasePlan ? getPlanPrice(selectedPurchasePlan, purchaseBillingCycle) : null;
  const selectedPurchaseIntro = selectedPurchasePlan && purchaseBillingCycle === 'monthly'
    ? getIntroPrice(selectedPurchasePlan)
    : null;
  const selectedPurchasePriceAvailable = selectedPurchasePrice != null && selectedPurchasePrice > 0
    && Boolean(planPricingForCycle(pricingCatalog, selectedPurchasePlan?.id, purchaseBillingCycle));
  const purchaseMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPurchasePlan || !candidateId) throw new Error(t('seek.checkout.missingPlanOrUser'));
      if (!planBillingConfirmed) throw new Error(t('seek.checkout.billingConfirmationRequired'));
      setPurchaseState('processing');

      const renewalPrice = getPlanPrice(selectedPurchasePlan, purchaseBillingCycle);
      const introPrice = purchaseBillingCycle === 'monthly' ? getIntroPrice(selectedPurchasePlan) : null;
      const amount = introPrice != null ? introPrice : renewalPrice;
      const renewalPricing = planPricingForCycle(pricingCatalog, selectedPurchasePlan.id, purchaseBillingCycle);
      const introPricing = purchaseBillingCycle === 'monthly'
        ? planIntroPricing(pricingCatalog, selectedPurchasePlan.id)
        : null;

      if (!renewalPrice || !amount || !renewalPricing) throw new Error(t('seek.checkout.planPricingNotConfigured'));

      const consentBinding = buildBillingConsentBinding({
        planId: selectedPurchasePlan.id,
        billingCycle: purchaseBillingCycle,
        taxConfigId: pricingCatalog?.tax_config_id,
        duePricing: introPricing ?? renewalPricing,
        introPricing,
        renewalPricing,
        consentLanguage: i18n.resolvedLanguage ?? i18n.language,
      });
      if (!consentBinding) throw new Error(t('pricingDisplay.unavailable'));

      const { data: { session } } = await seekSupabase.auth.getSession();
      if (!session) throw new Error(t('seek.checkout.notAuthenticated'));
      const attempt: PaymentIntentKeyInput = {
        ownerType: 'candidate',
        ownerId: candidateId,
        paymentType: 'subscription',
        productKey: selectedPurchasePlan.id,
        billingCycle: purchaseBillingCycle,
      };

      const res = await fetch(buildFunctionUrl('create-payment'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
          apikey: getSupabasePublishableKey(),
        },
        body: JSON.stringify({
          owner_type: 'candidate',
          owner_id: candidateId,
          payment_type: 'subscription',
          idempotency_key: paymentIntentKey(attempt),
          amount_sar: amount,
          description: `ForkSeek ${selectedPurchasePlan.plan_name} - ${purchaseBillingCycle}`,
          callback_url: `${window.location.origin}/seek/settings/subscription`,
          billing_consent: consentBinding,
          metadata: {
            plan_id: selectedPurchasePlan.id,
            billing_cycle: purchaseBillingCycle,
            billing_consent_version: CONSENT_TEXT_VERSION,
            billing_consent_accepted: 'true',
            billing_confirmation_confirmed: 'true',
          },
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        const governedBillingError = billingConfirmationRequiredError(err);
        if (governedBillingError) throw governedBillingError;
        throw new Error(paymentErrorMessage(err, i18n.language, t('seek.checkout.paymentFailed')));
      }
      const data = await res.json();
      bindPaymentIntentKeyToTransaction(attempt, data.transaction_id);
      settlePaymentIntentKey(attempt, data.status);
      return data;
    },
    onSuccess: async (data) => {
      purchaseRequestInFlightRef.current = false;
      try {
        if (data.status === 'downgrade_scheduled') {
          setPurchaseDialogOpen(false);
          setPurchaseState('select');
          await queryClient.invalidateQueries({ queryKey: ['seek-subscription'] });
          toast.success(t('seek.subscription.downgrade.scheduledTitle'), {
            description: paymentErrorMessage(data, i18n.language, t('seek.subscription.downgrade.scheduledTitle')),
          });
          return;
        }
        if (data.payment_form) {
          setPurchaseDialogOpen(false);
          setPaymentForm(data.payment_form);
          setPurchaseState('payment');
          return;
        }
        const redirectUrl = getCheckoutRedirectUrl(data);
        if (redirectUrl) {
          window.location.href = redirectUrl;
        } else if (canActivateMockPaidCheckout(data)) {
          await activateMockPaidCheckout(seekSupabase, data);
          setPurchaseDialogOpen(false);
          setPurchaseState('select');
          queryClient.invalidateQueries({ queryKey: ['seek-subscription'] });
          queryClient.invalidateQueries({ queryKey: ['seek-transactions'] });
          queryClient.invalidateQueries({ queryKey: ['seek-subscription-invoices'] });
          toast.success(t('seek.checkout.localMockPaymentActivated'));
        } else {
          throw new Error(t('seek.checkout.checkoutUnavailable'));
        }
      } catch (err) {
        setPurchaseState('select');
        showAppError(err as Error, { product: 'forkseek', domain: 'subscription' });
      }
    },
    onError: (err: Error) => {
      purchaseRequestInFlightRef.current = false;
      setPurchaseState('select');
      if (isBillingConfirmationRequiredError(err)) {
        setPurchaseDialogOpen(false);
        setPlanBillingConfirmed(false);
        setPlanBillingConfirmationOpen(true);
        toast.error(t('seek.checkout.billingInformationIncomplete'), {
          description: t('seek.checkout.completeBillingInformation'),
        });
        return;
      }
      showAppError(err, { product: 'forkseek', domain: 'subscription' });
    },
  });
  const creditPurchaseMutation = useMutation({
    retry: false,
    mutationFn: async () => {
      if (!creditCartPricing.ready) throw new Error(t('pricingDisplay.unavailable'));
      const cartItems = Object.entries(creditCart).map(([packId, quantity]) => ({ packId, quantity }));
      if (cartItems.length === 0) throw new Error(t('seek.credits.cart.emptyError'));
      const creditPricingBinding = buildCreditPricingBinding(cartItems, creditCartPricing.quote);
      if (!creditPricingBinding) throw new Error(t('pricingDisplay.unavailable'));

      const selectedPacks = cartItems.map((item) => ({
        pack: findCreditPackById(item.packId),
        quantity: item.quantity,
      }));
      if (selectedPacks.some((item) => !item.pack)) throw new Error(t('seek.credits.cart.packNotFoundError'));
      const completeSelectedPacks = selectedPacks.filter(hasCreditPack);

      const amount = completeSelectedPacks.reduce(
        (sum, item) => sum + Number(item.pack.price_sar) * item.quantity,
        0,
      );

      const { data: { session } } = await seekSupabase.auth.getSession();
      if (!session) throw new Error(t('seek.credits.notAuthenticated'));
      if (!candidateId) throw new Error(t('seek.credits.notAuthenticated'));
      const attempt = buildQuoteBoundCreditPaymentAttempt({
        ownerType: 'candidate',
        ownerId: candidateId,
        items: cartItems,
        pricingBinding: creditPricingBinding,
      });

      const res = await fetch(buildFunctionUrl('create-payment'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
          apikey: getSupabasePublishableKey(),
        },
        body: JSON.stringify({
          owner_type: 'candidate',
          owner_id: candidateId,
          payment_type: 'credit_pack',
          idempotency_key: paymentIntentKey(attempt),
          amount_sar: amount,
          description: t('seek.credits.cart.paymentDescription', { count: cartItems.length }),
          callback_url: `${window.location.origin}/seek/settings/subscription`,
          credit_pricing_binding: creditPricingBinding,
          metadata: {
            billing_confirmation_confirmed: 'true',
            credit_cart_json: JSON.stringify(
              cartItems.map((item) => ({
                credit_pack_product_id: item.packId,
                quantity: item.quantity,
              })),
            ),
          },
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw creditCheckoutResponseError({
          httpStatus: res.status,
          payload: err,
          language: i18n.language,
          fallback: paymentErrorMessage(err, i18n.language, t('seek.credits.paymentFailed')),
        });
      }
      const data = await res.json();
      bindPaymentIntentKeyToTransaction(attempt, data.transaction_id);
      settlePaymentIntentKey(attempt, data.status);
      return { data, attempt };
    },
    onSuccess: async ({ data, attempt }) => {
      try {
        if (data.payment_form) {
          setCreditBillingDialogOpen(false);
          setCreditPaymentForm(data.payment_form);
          return;
        }
        const redirectUrl = getCheckoutRedirectUrl(data);
        if (redirectUrl) {
          window.location.href = redirectUrl;
          return;
        }
        if (!canActivateMockPaidCheckout(data)) {
          throw new Error(t('seek.credits.checkoutUnavailable'));
        }

        await activateMockPaidCheckout(seekSupabase, data);
        settlePaymentIntentKey(attempt, 'paid_and_activated');
        setCreditBillingDialogOpen(false);
        toast.success(t('seek.credits.localMockPaymentActivatedTitle'), {
          description: t('seek.credits.localMockPaymentActivatedDescription'),
        });
        setCreditCart({});
        queryClient.invalidateQueries({ queryKey: ['seek-credits'] });
        queryClient.invalidateQueries({ queryKey: ['forkseek-universal-wallet'] });
        queryClient.invalidateQueries({ queryKey: ['forkseek-universal-credit-ledger'] });
        queryClient.invalidateQueries({ queryKey: ['forkseek-credit-purchase-attempts'] });
      } catch (err) {
        showAppError(err as Error, { product: 'forkseek', domain: 'billing' });
      }
    },
    onError: async (err) => {
      if (isCreditPricingChangedError(err)) {
        await recoverCreditPricingChange({
          closeConfirmation: () => setCreditBillingDialogOpen(false),
          clearPaymentForm: () => setCreditPaymentForm(null),
          resetConfirmation: () => setCreditConfirmationRevision((revision) => revision + 1),
          invalidateQuote: () => queryClient.invalidateQueries({
            queryKey: ['pricing-display-quote', 'forkseek'],
            refetchType: 'none',
          }),
          refetchQuote: () => creditCartPricing.refetch(),
          notifyCustomer: () => toast.error(t('pricingDisplay.creditPriceChanged')),
        });
        return;
      }
      showAppError(err, { product: 'forkseek', domain: 'billing' });
    },
  });
  const purchaseFlowLocked = purchaseMutation.isPending || purchaseState === 'processing';
  const creditFlowLocked = creditBillingDialogOpen || creditPurchaseMutation.isPending;
  const creditCheckoutLocked = creditFlowLocked || !creditCartPricing.ready;
  const openSeekPlanPurchase = useCallback((targetPlan?: SeekPlan | null) => {
    if (
      !targetPlan ||
      targetPlan.is_free ||
      !getPlanPrice(targetPlan, purchaseBillingCycle) ||
      !planPricingForCycle(pricingCatalog, targetPlan.id, purchaseBillingCycle)
    ) return false;
    if (paidPlanSelectionRoute === 'paid_plan_change') {
      setSelectedPlanChangeTarget(targetPlan);
      return true;
    }
    if (paidPlanSelectionRoute === 'blocked_inconsistent_paid_lifecycle') {
      toast.error(t('commercialPlanChange.errors.unsupportedResult'));
      return true;
    }
    setSelectedPurchasePlan(targetPlan);
    setBillingConsent(false);
    setPlanBillingConfirmed(false);
    setPurchaseDialogOpen(false);
    setPlanBillingConfirmationOpen(true);
    return true;
  }, [getPlanPrice, paidPlanSelectionRoute, pricingCatalog, purchaseBillingCycle, t]);

  useEffect(() => {
    const requestedPlanKey = searchParams.get('plan');
    if (requestedPlanKey === null) {
      handledPlanContinuationRef.current = null;
      return;
    }
    if (!isSeekPlanContinuationReady({
      plansLoading,
      subscriptionLoading: subLoading,
      pricingLoading: pricingCatalogLoading,
      pricingError: pricingCatalogError,
      plansError,
      subscriptionError: subError,
    })) return;

    const continuationIdentity = requestedPlanKey.trim() || '__blank_plan__';
    if (handledPlanContinuationRef.current === continuationIdentity) return;
    handledPlanContinuationRef.current = continuationIdentity;

    const targetPlan = findSeekPlanContinuation(requestedPlanKey, seekPlans);
    if (targetPlan && targetPlan.id !== plan?.id && !openSeekPlanPurchase(targetPlan)) {
      handledPlanContinuationRef.current = null;
      return;
    }

    setSearchParams(removeSeekPlanContinuation(searchParams), { replace: true });
  }, [
    openSeekPlanPurchase,
    plan?.id,
    plansError,
    plansLoading,
    pricingCatalogLoading,
    pricingCatalogError,
    searchParams,
    seekPlans,
    setSearchParams,
    subError,
    subLoading,
  ]);
  const beginPlanPayment = () => {
    if (purchaseFlowLocked || purchaseRequestInFlightRef.current || !planBillingConfirmed) return;
    purchaseRequestInFlightRef.current = true;
    purchaseMutation.mutate();
  };
  const usageServices = getPlanBundleServices(
    plan ?? { id: 'free', plan_key: 'free', plan_name: null },
    plan?.id ? planServicesMap[plan.id] ?? [] : [],
    'forkseek',
  ).filter((service) => {
    const serviceKey = service.services?.service_key;
    return serviceKey && !serviceKey.startsWith('metadata_');
  });
  const isCancellationPending = !!subscription?.cancellation_requested_at;
  const isDowngraded = subscription?.status === 'downgraded';
  if (subError && subscriptionError) console.error('Seek subscription query failed:', subscriptionError);
  if (creditsError && creditsQueryError) console.error('Seek credits query failed:', creditsQueryError);
  if (billingHistoryError && billingHistoryQueryError) console.error('Seek billing history query failed:', billingHistoryQueryError);

  const accessStatusLabel = subscription?.status === 'payment_failed'
    ? t('seek.subscription.status.paymentAttentionNeeded')
    : subscription?.status === 'expired'
      ? t('seek.subscription.status.expired')
      : subscription?.status === 'downgraded'
        ? t('seek.subscription.status.downgraded')
        : subscription?.status === 'active'
          ? t('seek.subscription.status.active')
          : subscription?.status ? subscription.status.replace(/_/g, ' ') : t('seek.subscription.status.freeAccess');
  const accessLifecycleLabel = isCancellationPending
    ? t('seek.subscription.lifecycle.cancelsAtPeriodEnd')
    : subscription?.status === 'expired'
      ? t('seek.subscription.lifecycle.accessExpired')
      : subscription?.renewal_date
        ? t('seek.subscription.lifecycle.renewsAutomatically')
        : subscription?.ends_at
          ? t('seek.subscription.lifecycle.expiresAtPeriodEnd')
          : t('seek.subscription.lifecycle.noPaidRenewal');
  const cancellationEffectiveDate = resolveAuthoritativeCancellationDate(subscription);
  const nextAccessDate = isCancellationPending
    ? cancellationEffectiveDate
    : subscription?.renewal_date || subscription?.ends_at || null;
  const billingHistoryEntries = useMemo(
    () => buildBillingHistoryEntries(transactions, invoices, {
      limit: 3,
      ownerType: 'candidate',
      ownerId: candidateId,
    }),
    [transactions, invoices, candidateId],
  );

  const statusBadge = (status: string) => {
    if (status === 'active' || status === 'paid') return <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"><CheckCircle2 className="w-3 h-3 me-1" />{status === 'active' ? t('seek.subscription.status.active') : t('seek.subscription.status.paid')}</Badge>;
    if (status === 'pending') return <Badge variant="secondary"><Clock className="w-3 h-3 me-1" />{t('seek.subscription.status.pending')}</Badge>;
    if (status === 'failed' || status === 'payment_failed') return <Badge variant="destructive">{t('seek.subscription.status.failed')}</Badge>;
    if (status === 'downgraded') return <Badge variant="outline"><XCircle className="w-3 h-3 me-1" />{t('seek.subscription.status.downgraded')}</Badge>;
    return <Badge variant="outline">{status}</Badge>;
  };
  const legacyCreditsTotal = credits.reduce((sum, credit) => sum + Number(credit.balance ?? 0), 0);
  const walletCreditsTotal = Number(universalWalletRead.wallet?.balance ?? 0);
  const hasUniversalWallet = Boolean(universalWalletRead.wallet);
  const totalCredits = hasUniversalWallet ? walletCreditsTotal : legacyCreditsTotal;
  const creditsSummaryLoading = creditsLoading || universalWalletLoading;
  const creditsSummaryError = !hasUniversalWallet && creditsError;

  return (
    <SeekLayout>
      <div className="space-y-5 sm:space-y-6 lg:space-y-8 max-w-[1200px] 3xl:max-w-[1440px] mx-auto">
        <PaymentReturnBanner
          invalidateKeys={[
            ['seek-subscription'],
            ['seek-plan-usage'],
            ['seek-transactions'],
            ['seek-subscription-invoices'],
            ['seek-invoices'],
          ]}
          client={seekSupabase}
        />
        <Card className="rounded-xl p-4 sm:p-5 lg:p-6">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground font-display">{t('seek.subscription.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('seek.subscription.subtitle')}</p>
        </Card>

        {subLoading ? (
          <Skeleton className="h-40 rounded-xl" />
        ) : subError ? (
          <Card className="rounded-xl">
            <CardContent className="p-5">
              <SeekBillingSectionError
                title={t('seek.subscription.subscriptionLoadTitle')}
                message={t('seek.subscription.subscriptionLoadMessage')}
                retryLabel={t('common.retry')}
                onRetry={() => refetchSubscription()}
              />
            </CardContent>
          </Card>
        ) : (
          <>
            {isCancellationPending && !isDowngraded && (
              <Card className="rounded-xl border-yellow-500/50 bg-yellow-50 dark:bg-yellow-900/10">
                <CardContent className="p-4 flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-yellow-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold text-yellow-800 dark:text-yellow-300">{t('seek.subscription.cancellationScheduled')}</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {t('seek.subscription.cancellationScheduledDesc', { date: cancellationEffectiveDate ? formatLocalizedDate(cancellationEffectiveDate, 'MMM dd, yyyy', language) : t('seek.subscription.endOfPeriod') })}
                      </p>
                    </div>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className="shrink-0 rounded-xl border-yellow-600/40 text-yellow-800 hover:bg-yellow-100 dark:text-yellow-300 dark:hover:bg-yellow-900/30"
                        disabled={revokeCancelMutation.isPending}
                      >
                        {revokeCancelMutation.isPending ? t('seek.subscription.reverting') : t('seek.subscription.keepMySubscription')}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t('seek.subscription.revokeCancellationTitle')}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t('seek.subscription.revokeCancellationDescPrefix')}{' '}
                          <strong>{cancellationEffectiveDate ? formatLocalizedDate(cancellationEffectiveDate, 'MMM dd, yyyy', language) : '-'}</strong>.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t('common.close')}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => revokeCancelMutation.mutate()}>
                          {t('seek.subscription.yesKeepSubscription')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </CardContent>
              </Card>
            )}

            {subscription?.status === 'active' && plan && !plan.is_free && (
              <SubscriptionCancellationManagement
                portal="forkseek"
                currentPlan={plan.plan_name}
                pending={isCancellationPending}
                authoritativeDate={cancellationEffectiveDate}
                paidThroughDate={subscription.paid_through_date}
                autoRenewEnabled={subscription.auto_renew !== false}
                downgradeCleared={Boolean(subscription.cancellation_cleared_downgrade_plan_id)}
                formatDate={(value) => value ? formatLocalizedDate(value, 'MMM dd, yyyy', language) : '-'}
                cancelling={cancelMutation.isPending}
                undoing={revokeCancelMutation.isPending}
                onCancel={() => cancelMutation.mutate()}
                onUndo={() => revokeCancelMutation.mutate()}
              />
            )}

            <Card className="shadow-card overflow-hidden rounded-2xl border-border">
              <div className="p-5 pb-4 sm:p-6">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="flex items-center gap-2 text-base font-bold font-display text-foreground">
                      <Package className="w-5 h-5 text-seek" />
                      {t('seek.subscription.availablePlans')}
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">{t('seek.subscription.availablePlansDesc')}</p>
                  </div>
                  <Badge variant="outline" className="w-fit border-seek/30 text-seek">
                    {t('seek.subscription.plansVisibleUpfront')}
                  </Badge>
                </div>
              </div>
              <div className="px-5 pb-5 sm:px-6 sm:pb-6">
                {plansLoading ? (
                  <div className="grid gap-4" style={adaptivePlanGridStyle}>
                    {[1, 2, 3].map((i) => <Skeleton key={i} className="h-72 rounded-xl" />)}
                  </div>
                ) : plansError ? (
                  <SeekBillingSectionError
                    title={t('seek.subscription.plansLoadTitle')}
                    message={t('seek.subscription.plansLoadMessage')}
                    retryLabel={t('common.retry')}
                    onRetry={() => refetchPlans()}
                  />
                ) : visiblePlans.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">{t('seek.subscription.noPlansAvailable')}</p>
                ) : (
                  <div className="grid items-start gap-4" style={adaptivePlanGridStyle}>
                    {visiblePlans.map((p) => {
                      const price = getPlanPrice(p, purchaseBillingCycle);
                      const intro = purchaseBillingCycle === 'monthly' ? getIntroPrice(p) : null;
                      const isCurrent = plan?.id === p.id || (!plan && p.is_free);
                      const priceAvailable = p.is_free || (
                        price != null &&
                        Boolean(planPricingForCycle(pricingCatalog, p.id, purchaseBillingCycle))
                      );
                      const services = getPlanBundleServices(p, planServicesMap[p.id] ?? [], 'forkseek');

                      return (
                        <div
                          key={p.id}
                          className={`relative flex h-full min-w-0 flex-col rounded-lg border border-border bg-background p-4 transition-all ${isCurrent ? 'ring-2 ring-seek shadow-md' : 'hover:shadow-md'} ${p.is_popular && !isCurrent ? 'shadow-md ring-2 ring-warning/80' : ''}`}
                        >
                          {p.is_popular && (
                            <div className="absolute -top-3 start-1/2 z-10 -translate-x-1/2 rtl:translate-x-1/2">
                              <Badge className="bg-warning px-3 py-0.5 text-[10px] text-warning-foreground shadow-sm">
                                {t('seek.checkout.mostPopular')}
                              </Badge>
                            </div>
                          )}
                          {isCurrent && (
                            <div className="absolute top-3 end-3">
                              <Badge variant="outline" className="border-seek/30 text-seek">
                                {t('seek.subscription.currentPlan')}
                              </Badge>
                            </div>
                          )}
                          <div className="pb-3 pt-1">
                            <h3 className="pe-20 text-lg font-bold text-foreground">{p.plan_name}</h3>
                            {p.description && <p className="mt-1 line-clamp-4 text-sm leading-relaxed text-muted-foreground">{p.description}</p>}
                          </div>
                          <div className="flex flex-1 flex-col gap-4">
                            <div>
                              {p.is_free ? (
                                <p className="text-3xl font-bold text-foreground">{t('seek.subscription.free')}</p>
                              ) : priceAvailable ? (
                                <>
                                  <PlanGrossPrice platform="forkseek" planId={p.id} billingCycle={purchaseBillingCycle} introductory={intro != null && intro !== price} />
                                  {intro != null && intro !== price && (
                                    <div className="mt-1 text-xs text-muted-foreground"><PlanGrossPrice platform="forkseek" planId={p.id} billingCycle={purchaseBillingCycle} /></div>
                                  )}
                                </>
                              ) : (
                                <div className="space-y-1">
                                  <p className="text-lg font-semibold text-foreground">{pricingCatalogLoading ? t('pricingDisplay.loading') : t('seek.checkout.pricingUnavailable')}</p>
                                  <p className="text-xs text-muted-foreground">{t('seek.checkout.pricingUnavailableDesc')}</p>
                                </div>
                              )}
                            </div>

                            <PlanBundleShowcase
                              services={services}
                              translationScope="seek.checkout"
                              brand="seek"
                              isSelected={isCurrent}
                            />

                            <Button
                              type="button"
                              className={isCurrent ? 'mt-auto rounded-xl' : 'mt-auto rounded-xl bg-seek text-white hover:bg-seek/90'}
                              variant={isCurrent ? 'outline' : 'default'}
                              disabled={isCurrent || p.is_free || !priceAvailable}
                              onClick={() => openSeekPlanPurchase(p)}
                            >
                              {isCurrent
                                ? t('seek.subscription.currentPlan')
                                : p.is_free
                                  ? t('seek.subscription.free')
                                  : paidPlanSelectionRoute === 'paid_plan_change'
                                    ? t('commercialPlanChange.changePlan')
                                    : t('seek.subscription.choosePlan')}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </Card>

            {plan && !isDowngraded && (
              <Card className="rounded-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Package className="w-5 h-5 text-seek" />{t('seek.subscription.currentPlan')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-xl font-bold">{plan.plan_name}</h3>
                        <p className="text-sm text-muted-foreground">{plan.description || t('seek.subscription.candidateSubscription')}</p>
                      </div>
                      {statusBadge(subscription.status)}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">{t('seek.subscription.price')}</p>
                        <PlanGrossPrice platform="forkseek" planId={plan.id} billingCycle={activeBillingCycle} />
                      </div>
                      <div>
                        <p className="text-muted-foreground">{t('seek.subscription.renewal')}</p>
                        <p className="font-semibold">{subscription.renewal_date ? formatLocalizedDate(subscription.renewal_date, 'MMM dd, yyyy', language) : '-'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">{t('seek.subscription.payment')}</p>
                        <p className="font-semibold capitalize">{subscription.payment_method || t('seek.subscription.notSet')}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 rounded-lg border border-border bg-muted/20 p-3 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground">{t('seek.subscription.accessStatus')}</p>
                        <p className="font-semibold text-foreground capitalize">{accessStatusLabel}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">{t('seek.subscription.renewalState')}</p>
                        <p className="font-semibold text-foreground">{accessLifecycleLabel}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">{t('seek.subscription.nextRenewalOrExpiry')}</p>
                        <p className="font-semibold text-foreground">{nextAccessDate ? formatLocalizedDate(nextAccessDate, 'MMM dd, yyyy', language) : '-'}</p>
                      </div>
                    </div>
                    <Separator />
                    <div className="flex gap-2 flex-wrap">
                      {nextUpgradePlan && (
                        <Button
                          variant="outline"
                          className="rounded-xl"
                          onClick={() => openSeekPlanPurchase(nextUpgradePlan)}
                        >
                          <ArrowUpRight className="w-4 h-4 me-1.5 rtl-flip" />{t('seek.subscription.upgradePlan')}
                        </Button>
                      )}
                      <CandidateDowngradeSection subscription={subscription} currentPlan={plan} allPlans={seekPlans} candidateId={candidateId} />
                      {subscription.status === 'active' && !plan.is_free && !isCancellationPending && (
                        <RecurringPaymentMethodDialog
                          client={seekSupabase}
                          ownerType="candidate"
                          subscriptionId={subscription.id}
                          onAuthorized={() => queryClient.invalidateQueries({ queryKey: ['seek-subscription'] })}
                        />
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card id="seek-credit-bundles" className="scroll-mt-24 rounded-xl">
              <CardContent className="space-y-6 p-5 sm:p-6">
                <div>
                  <div className="mb-4 flex items-center justify-between gap-4">
                    <h2 className="font-display text-base font-bold text-foreground">{t('seek.subscription.creditsRemainingTitle')}</h2>
                    <span className="font-display text-2xl font-bold text-foreground">{creditsSummaryError ? '-' : totalCredits}</span>
                  </div>

                  <Progress value={!creditsSummaryError && totalCredits > 0 ? 100 : 0} className="mb-5 h-2 [&>div]:bg-seek" />

                  {creditsSummaryLoading ? (
                    <Skeleton className="h-24 rounded-xl" />
                  ) : creditsSummaryError ? (
                    <SeekBillingSectionError
                      title={t('seek.subscription.creditsLoadTitle')}
                      message={t('seek.subscription.creditsLoadMessage')}
                      retryLabel={t('common.retry')}
                      onRetry={() => {
                        refetchCredits();
                        refetchUniversalWallet();
                      }}
                    />
                  ) : (
                    <div className="space-y-4">
                      {hasUniversalWallet && (
                        <div className="flex items-center justify-between gap-4">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-foreground">{t('seek.credits.candidateWalletTitle')}</p>
                            <p className="text-xs text-muted-foreground">{t('seek.subscription.remaining')}</p>
                          </div>
                          <span className="shrink-0 text-lg font-semibold text-foreground">{walletCreditsTotal}</span>
                        </div>
                      )}

                      {credits.map((c) => (
                        <div key={c.id} className="flex items-center justify-between gap-4">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-foreground capitalize">{c.credit_type.replace(/_/g, ' ')}</p>
                            <p className="text-xs text-muted-foreground">{t('seek.subscription.remaining')}</p>
                          </div>
                          <span className="shrink-0 text-lg font-semibold text-foreground">{c.balance}</span>
                        </div>
                      ))}

                      {!hasUniversalWallet && credits.length === 0 && (
                        <p className="text-sm text-muted-foreground">{t('seek.subscription.noCreditsPurchased')}</p>
                      )}
                    </div>
                  )}
                </div>

                <Separator />

              {creditCartItems.length > 0 && (
                  <div ref={creditCartRef} className="rounded-xl border border-seek/30 p-4 sm:p-5">
                    <div className="pb-3">
                      <h3 className="text-base font-semibold flex items-center gap-2">
                      <ShoppingCart className="w-4 h-4 text-seek" />
                      {t('seek.credits.cart.title')}
                      </h3>
                      <p className="text-sm text-muted-foreground">{t('seek.credits.cart.description')}</p>
                    </div>
                    <div className="space-y-3">
                    {creditCartItems.map(({ pack, quantity }) => (
                      <div key={pack.id} className="flex flex-col gap-3 rounded-lg border border-border p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          <p className="font-medium text-foreground">{pack.name}</p>
                          <div className="text-xs text-muted-foreground">
                            {t('seek.credits.cart.eachLabel')}
                            <CreditCartLinePricing quote={creditCartPricing.quote} packId={pack.id} value="unit" />
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center justify-between gap-3 sm:justify-end">
                          <div className="flex items-center gap-1 rounded-full border border-input p-1">
                            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCreditPackQuantity(pack.id, quantity - 1)} disabled={creditFlowLocked} aria-label={t('seek.credits.cart.decreasePack', { name: pack.name })}>
                              <Minus className="h-3.5 w-3.5" />
                            </Button>
                            <span className="min-w-8 text-center font-semibold">{quantity}</span>
                            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCreditPackQuantity(pack.id, quantity + 1)} disabled={creditFlowLocked} aria-label={t('seek.credits.cart.increasePack', { name: pack.name })}>
                              <Plus className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          <CreditCartLinePricing quote={creditCartPricing.quote} packId={pack.id} value="line" />
                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setCreditPackQuantity(pack.id, 0)} disabled={creditFlowLocked} aria-label={t('seek.credits.cart.removePack', { name: pack.name })}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <CreditCartPricingStatus ready={creditCartPricing.ready} reason={creditCartPricing.reason} />
                    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
                      <div className="font-semibold">{t('seek.credits.cart.total')}<CreditCartPricing quote={creditCartPricing.quote} /></div>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={clearCreditCart} disabled={creditFlowLocked}>{t('seek.credits.cart.clear')}</Button>
                        <Button type="button" className="bg-seek text-white hover:bg-seek/90" onClick={() => setCreditBillingDialogOpen(true)} disabled={creditCheckoutLocked}>
                          {creditPurchaseMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin me-1.5" /> : <ShoppingCart className="w-4 h-4 me-1.5" />}
                          {t('seek.credits.cart.checkout')}
                        </Button>
                      </div>
                    </div>
                    </div>
                  </div>
              )}

                <div>
                  <div className="mb-4">
                    <h3 className="flex items-center gap-2 font-display text-xl font-bold text-foreground">
                    <Coins className="w-5 h-5 text-seek" />
                    {t('seek.credits.topUpTitle')}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">{t('seek.credits.topUpSubtitle')}</p>
                  </div>
                  {creditPacksLoading ? (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {[1, 2, 3].map((i) => <Skeleton key={i} className="h-44 rounded-xl" />)}
                    </div>
                  ) : creditPacksError ? (
                    <SeekBillingSectionError
                      title={t('seek.credits.creditPacksLoadTitle')}
                      message={t('seek.credits.creditPacksLoadMessage')}
                      retryLabel={t('common.retry')}
                      onRetry={() => refetchCreditPacks()}
                    />
                  ) : creditPacks.length === 0 ? (
                    <div className="rounded-xl border border-border bg-muted/20 py-8 text-center text-muted-foreground">
                      {t('seek.credits.noCreditPacks')}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {creditPacks.map((pack) => {
                        const packId = getCreditPackId(pack);
                        const packQuantity = creditCart[packId] ?? 0;
                        return (
                        <Card key={packId} className="rounded-xl transition-shadow hover:shadow-md">
                          <CardHeader className="pb-2">
                            <div className="flex items-start justify-between gap-3">
                              <CardTitle className="text-base">{pack.name}</CardTitle>
                              {packQuantity > 0 && (
                                <Badge className="border-0 bg-seek/10 text-seek">
                                  {t('seek.credits.cart.inCart', { count: packQuantity })}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{t('seek.credits.packCredits', { count: pack.credits_included })}</p>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <CreditPackGrossPrice platform="forkseek" packId={pack.id} />
                            <p className="text-xs text-muted-foreground">
                              {t('seek.credits.balanceLabel')}: <span className="font-semibold text-foreground">{credits.find((credit) => credit.credit_type === pack.credit_type)?.balance ?? 0}</span>
                              {pack.valid_days && t('seek.credits.validDays', { days: pack.valid_days })}
                            </p>
                            {packQuantity > 0 && (
                              <div className="flex items-center justify-between rounded-xl border border-seek/20 bg-seek/5 px-3 py-2">
                                <span className="text-sm font-medium text-foreground">{t('seek.credits.cart.quantity')}</span>
                                <div className="flex items-center gap-1 rounded-full border border-input bg-background p-1">
                                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCreditPackQuantity(packId, packQuantity - 1)} disabled={creditFlowLocked} aria-label={t('seek.credits.cart.decreasePack', { name: pack.name })}>
                                    <Minus className="h-3.5 w-3.5" />
                                  </Button>
                                  <span className="min-w-8 text-center text-sm font-semibold">{packQuantity}</span>
                                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCreditPackQuantity(packId, packQuantity + 1)} disabled={creditFlowLocked} aria-label={t('seek.credits.cart.increasePack', { name: pack.name })}>
                                    <Plus className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </div>
                            )}
                            <Button type="button" className="w-full rounded-xl bg-seek text-white hover:bg-seek/90" onClick={() => addCreditPackToCart(packId, pack.name)} disabled={creditFlowLocked}>
                              <ShoppingCart className="w-4 h-4 me-1.5" />
                              {packQuantity > 0 ? t('seek.credits.cart.addAnother') : t('seek.credits.cart.addToCart')}
                            </Button>
                          </CardContent>
                        </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-seek" />
                  {t('seek.subscription.usageTitle')}
                </CardTitle>
                <p className="text-sm text-muted-foreground">{t('seek.subscription.usageSubtitle')}</p>
              </CardHeader>
              <CardContent>
                {usageServices.length === 0 ? (
                  <p className="py-4 text-center text-sm text-muted-foreground">{t('seek.subscription.noUsageServices')}</p>
                ) : (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {usageServices.map((service, index) => {
                      const serviceKey = service.services?.service_key || `${service.plan_id}-${index}`;
                      return (
                        <SeekUsageServiceCard
                          key={`${serviceKey}-${index}`}
                          candidateId={candidateId}
                          serviceKey={serviceKey}
                          serviceName={t(`seek.checkout.serviceNames.${serviceKey}`, {
                            defaultValue: service.services?.service_name || t('seek.checkout.serviceFallback'),
                          })}
                        />
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <Collapsible>
              <Card className="overflow-hidden rounded-xl border-border">
                <CollapsibleTrigger className="flex w-full cursor-pointer items-center justify-between px-5 py-4 text-start transition-colors hover:bg-seek/5 sm:px-6">
                  <span className="text-sm font-semibold text-foreground">{t('seek.subscription.howBillingWorksTitle')}</span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="space-y-4 border-t border-seek/30 px-5 pb-5 pt-4 text-sm text-muted-foreground sm:px-6 sm:pb-6">
                    <div>
                      <p className="mb-1 font-semibold text-foreground">{t('seek.subscription.upgradesTitle')}</p>
                      <p>{t('seek.subscription.upgradesDescription')}</p>
                    </div>
                    <div>
                      <p className="mb-1 font-semibold text-foreground">{t('seek.subscription.downgradesTitle')}</p>
                      <p>{t('seek.subscription.downgradesDescription')}</p>
                    </div>
                    <div>
                      <p className="mb-1 font-semibold text-foreground">{t('seek.subscription.creditsExplainerTitle')}</p>
                      <p>{t('seek.subscription.creditsExplainerDescription')}</p>
                    </div>
                  </div>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            <BillingHistoryCard
              entries={billingHistoryEntries}
              brand="forkseek"
              loading={billingHistoryLoading}
              error={billingHistoryError}
              summary
              viewAllTo="/seek/settings/invoices"
              retryLabel={t('common.retry')}
              errorTitle={t('seek.subscription.paymentHistoryLoadTitle')}
              errorMessage={t('seek.subscription.paymentHistoryLoadMessage')}
              onRetry={refetchBillingHistory}
              onOpenInvoice={(invoice) => setSelectedInvoice(invoice as InvoiceRecord)}
            />
          </>
        )}
      </div>

      <InvoiceViewerDialog
        invoice={selectedInvoice}
        open={!!selectedInvoice}
        onOpenChange={(open) => {
          if (!open) setSelectedInvoice(null);
        }}
      />
      {selectedPurchasePlan && (
        <BillingConfirmationDialog
          open={planBillingConfirmationOpen}
          onOpenChange={(open) => {
            if (!purchaseFlowLocked) setPlanBillingConfirmationOpen(open);
          }}
          ownerType="candidate"
          ownerId={candidateId}
          client={seekSupabase}
          editPath="/seek/profile"
          candidateIdentityEditPath="/seek/account"
          brand="forkseek"
          pricingSummary={(
            <div className="rounded-lg border border-border p-4">
              <p className="text-sm font-semibold text-foreground">{selectedPurchasePlan.plan_name}</p>
              <div className="mt-1">
                <PlanGrossPrice
                  platform="forkseek"
                  planId={selectedPurchasePlan.id}
                  billingCycle={purchaseBillingCycle}
                  introductory={selectedPurchaseIntro != null && selectedPurchaseIntro !== selectedPurchasePrice}
                />
              </div>
            </div>
          )}
          pricingReady={selectedPurchasePriceAvailable}
          pricingLoading={pricingCatalogLoading}
          onConfirm={() => {
            setPlanBillingConfirmed(true);
            setPlanBillingConfirmationOpen(false);
            setPurchaseDialogOpen(true);
          }}
        />
      )}
      {selectedPurchasePlan && (
        <CheckoutPurchaseDialog
          open={purchaseDialogOpen}
          onOpenChange={(open) => {
            if (!purchaseMutation.isPending) setPurchaseDialogOpen(open);
          }}
          translationScope="seek.checkout"
          brand="seek"
          platform="forkseek"
          planId={selectedPurchasePlan.id}
          planName={selectedPurchasePlan.plan_name}
          billingCycle={purchaseBillingCycle}
          introPrice={selectedPurchaseIntro}
          renewalPrice={selectedPurchasePrice}
          priceAvailable={selectedPurchasePriceAvailable}
          billingConsent={billingConsent}
          onBillingConsentChange={setBillingConsent}
          disabled={purchaseFlowLocked}
          isProcessing={purchaseMutation.isPending}
          confirmLabel={t('seek.checkout.confirmAndPay')}
          onConfirm={beginPlanPayment}
        />
      )}
      <ActiveSubscriptionPlanChangeDialog
        open={!!selectedPlanChangeTarget}
        onOpenChange={(open) => {
          if (!open) setSelectedPlanChangeTarget(null);
        }}
        ownerType="candidate"
        ownerId={candidateId}
        portal="forkseek"
        targetPlan={selectedPlanChangeTarget}
        client={seekSupabase}
        callbackUrl={`${window.location.origin}/seek/settings/subscription`}
        editBillingPath="/seek/profile"
      />
      <SecurePaymentDialog
        open={!!paymentForm && (purchaseState === 'payment' || purchaseState === 'processing')}
        config={paymentForm}
        client={seekSupabase}
        brand="forkseek"
        title={t('seek.checkout.paymentDetails')}
        onBack={() => {
          setPaymentForm(null);
          setPurchaseState('select');
        }}
      />
      <SecurePaymentDialog
        open={!!creditPaymentForm}
        config={creditPaymentForm}
        client={seekSupabase}
        brand="forkseek"
        title={t('seek.credits.paymentDetails')}
        onBack={() => setCreditPaymentForm(null)}
      />
      <BillingConfirmationDialog
        key={`credit-confirmation-${creditConfirmationRevision}`}
        open={creditBillingDialogOpen}
        onOpenChange={setCreditBillingDialogOpen}
        ownerType="candidate"
        ownerId={candidateId}
        client={seekSupabase}
        editPath="/seek/profile"
        brand="forkseek"
        isProcessing={creditPurchaseMutation.isPending}
        pricingSummary={<CreditCartPricing quote={creditCartPricing.quote} breakdown />}
        pricingReady={creditCartPricing.ready}
        pricingLoading={creditCartPricing.reason === 'loading'}
        onConfirm={() => creditPurchaseMutation.mutate()}
      />
    </SeekLayout>
  );
}
